import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowRight, BadgeCheck, FileText, CreditCard, Syringe, Bandage, Droplets, TestTube, Pill, Heart } from 'lucide-react';
import { nursingServices } from '../data/mockData';

export default function SoinsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-50 to-neutral-50 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-neutral-900 mb-6">
              Soins infirmiers à domicile à Fleurus et environs
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed">
              Une gamme complète de soins techniques et d'accompagnement, tous remboursés par l'INAMI 
              sur prescription médicale.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8">
            {nursingServices.map((service) => {
              const iconMap = {
                'injections': Syringe,
                'wound-care': Bandage,
                'personal-hygiene': Droplets,
                'blood-samples': TestTube,
                'medication-management': Pill,
                'elderly-care': Heart
              };
              const IconComponent = iconMap[service.id] || Heart;
              
              return (
                <Card key={service.id} className="border-neutral-200 hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-2xl text-neutral-900 mb-2">
                          {service.title}
                        </CardTitle>
                        <p className="text-neutral-600 leading-relaxed">
                          {service.description}
                        </p>
                      </div>
                      <div className="ml-4 bg-gradient-to-br from-amber-50 to-amber-100 w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-7 h-7 text-amber-700" />
                      </div>
                    </div>
                  </CardHeader>
                
                <CardContent>
                  <div className="space-y-4">
                    {/* Details List */}
                    {service.details && service.details.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-neutral-900 mb-3">Types de soins :</h4>
                        <ul className="grid md:grid-cols-2 gap-2">
                          {service.details.map((detail, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-amber-600 mr-2">•</span>
                              <span className="text-neutral-700">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Badges */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t border-neutral-100">
                      {service.requiresPrescription && (
                        <div className="inline-flex items-center text-sm text-neutral-700 bg-neutral-50 px-3 py-1.5 rounded-full">
                          <FileText className="h-4 w-4 mr-2" />
                          Prescription médicale requise
                        </div>
                      )}
                      {service.inami && (
                        <div className="inline-flex items-center text-sm text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full font-medium">
                          <BadgeCheck className="h-4 w-4 mr-2" />
                          Remboursement INAMI
                        </div>
                      )}
                    </div>
                    
                    {/* CTA */}
                    <div className="pt-4">
                      <Link to="/contact">
                        <Button variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50">
                          Demander ce soin
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* INAMI Information */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-amber-50 to-neutral-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-amber-200 bg-white shadow-lg">
            <CardHeader>
              <div className="flex items-center space-x-3 mb-2">
                <div className="bg-amber-100 p-2 rounded-lg">
                  <BadgeCheck className="h-6 w-6 text-amber-700" />
                </div>
                <CardTitle className="text-2xl text-neutral-900">
                  Conventionnement et remboursement
                </CardTitle>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CreditCard className="h-5 w-5 text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-neutral-900 mb-1">Infirmière conventionnée INAMI</h4>
                    <p className="text-neutral-600 leading-relaxed">
                      Tous les soins sont remboursés selon les barèmes officiels de l'INAMI. 
                      Vous ne payez que le ticket modérateur.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <FileText className="h-5 w-5 text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-neutral-900 mb-1">Prescription médicale obligatoire</h4>
                    <p className="text-neutral-600 leading-relaxed">
                      Une prescription de votre médecin traitant est nécessaire pour tous les soins techniques. 
                      Elle garantit le remboursement par votre mutuelle.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <BadgeCheck className="h-5 w-5 text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-neutral-900 mb-1">Tiers payant possible</h4>
                    <p className="text-neutral-600 leading-relaxed">
                      Selon votre situation (statut BIM, affection chronique), le tiers payant peut être appliqué. 
                      Vous ne payez alors rien lors de la visite.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <p className="text-sm text-neutral-700 leading-relaxed">
                  <span className="font-semibold">Important :</span> Les soins sont réalisés dans le strict respect 
                  des prescriptions médicales et des protocoles en vigueur. Une coordination étroite avec votre 
                  médecin traitant assure un suivi optimal de votre état de santé.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">
              Questions fréquentes
            </h2>
            <p className="text-lg text-neutral-600">
              Les réponses à vos questions sur les soins infirmiers à domicile
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="prescription" className="border border-neutral-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-neutral-900 hover:no-underline">
                Ai-je besoin d'une prescription pour tous les soins ?
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 leading-relaxed">
                Oui, une prescription médicale est obligatoire pour tous les soins infirmiers techniques 
                (injections, pansements, prises de sang, etc.). Cette prescription doit être établie par 
                votre médecin traitant et préciser le type de soins ainsi que leur fréquence.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="remboursement" className="border border-neutral-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-neutral-900 hover:no-underline">
                Comment fonctionne le remboursement par la mutuelle ?
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 leading-relaxed">
                En tant qu'infirmière conventionnée INAMI, tous les soins prescrits sont remboursés par 
                votre mutuelle selon les barèmes officiels. Vous ne payez que le ticket modérateur 
                (quote-part personnelle). Le tiers payant est possible selon votre situation.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="frequency" className="border border-neutral-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-neutral-900 hover:no-underline">
                À quelle fréquence puis-je bénéficier de soins à domicile ?
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 leading-relaxed">
                La fréquence des soins dépend de votre prescription médicale et de vos besoins spécifiques. 
                Les soins peuvent être quotidiens, plusieurs fois par semaine, ou ponctuels selon votre 
                situation. Le planning est adapté à vos contraintes horaires.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="emergency" className="border border-neutral-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-neutral-900 hover:no-underline">
                Que faire en cas d'urgence en dehors des horaires ?
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 leading-relaxed">
                Pour les urgences en dehors des horaires d'ouverture, contactez-moi par téléphone. 
                En cas d'urgence vitale, appelez toujours le 112. Pour les soins urgents mais non vitaux 
                le week-end, je m'efforce de trouver une solution adaptée.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="equipment" className="border border-neutral-200 rounded-lg px-6">
              <AccordionTrigger className="text-left font-semibold text-neutral-900 hover:no-underline">
                Dois-je fournir du matériel médical ?
              </AccordionTrigger>
              <AccordionContent className="text-neutral-600 leading-relaxed">
                Non, j'apporte tout le matériel stérile nécessaire pour les soins (compresses, gants, 
                seringues, etc.). Seuls certains traitements spécifiques prescrits par votre médecin 
                (médicaments, pansements particuliers) sont à votre charge ou remboursés par la pharmacie.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-amber-600 to-amber-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Besoin d'un soin spécifique ?
          </h2>
          <p className="text-xl text-amber-50 mb-8">
            Contactez-moi pour discuter de vos besoins et obtenir un devis personnalisé
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-amber-700 hover:bg-amber-50">
              Prendre contact
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
